import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Send, Sparkles, AlertTriangle, Stethoscope, BookOpen } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

const sessionId = (() => {
    const k = "hp_ai_session";
    let id = localStorage.getItem(k);
    if (!id) { id = uuidv4(); localStorage.setItem(k, id); }
    return id;
})();

export default function AIDoctor() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [refer, setRefer] = useState(false);
    const [products, setProducts] = useState([]);
    const [articles, setArticles] = useState([]);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

    useEffect(() => {
        // Initial greeting
        setMessages([{ role: "assistant", content: "Hello — I'm Dr. Hahnemann AI. Tell me what you're feeling today: your main symptom, when it started, and what makes it better or worse." }]);
    }, []);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/symptom-check", { session_id: sessionId, message: msg });
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
            setRefer(data.refer_to_doctor);
            setProducts(data.suggested_products || []);
            setArticles(data.suggested_articles || []);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach my brain right now. Please try again in a moment." }]);
        } finally {
            setBusy(false);
        }
    };

    const samplePrompts = [
        "Sudden high fever with red face, dry skin and throbbing headache",
        "Runny nose and sneezing in cold weather, worse after midnight",
        "Burning indigestion with sour belching after spicy food",
    ];

    return (
        <div className="relative" data-testid="ai-doctor-page">
            <div className="absolute inset-0 paper opacity-50 pointer-events-none" />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 relative">
                <div className="text-center mb-10">
                    <div className="label-eyebrow text-sky-700 inline-flex items-center gap-2"><Sparkles className="w-3 h-3" /> Dr. Hahnemann AI</div>
                    <h1 className="font-serif text-5xl text-stone-900 mt-3">Describe what you feel.</h1>
                    <p className="text-sm text-stone-600 mt-3 max-w-xl mx-auto">A private, classical-homeopathy interview. Not a substitute for a real doctor.</p>
                </div>

                <div className="bg-white border border-stone-200 min-h-[480px] flex flex-col">
                    <div className="flex-1 p-6 space-y-5 overflow-y-auto max-h-[60vh]">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-sky-700 text-stone-50" : "bg-stone-100 text-stone-800 border border-stone-200"}`} data-testid={`msg-${m.role}-${i}`}>
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {busy && <div className="flex justify-start"><div className="bg-stone-100 border border-stone-200 px-4 py-3 text-sm text-stone-500">Dr. Hahnemann is thinking…</div></div>}
                        <div ref={endRef} />
                    </div>

                    {messages.length === 1 && (
                        <div className="px-6 pb-4 flex flex-wrap gap-2">
                            {samplePrompts.map((s) => (
                                <button key={s} onClick={() => send(s)} className="text-xs border border-stone-300 px-3 py-1.5 hover:border-sky-700 hover:text-sky-700" data-testid={`prompt-${s.slice(0, 12)}`}>{s.length > 60 ? s.slice(0, 60) + "…" : s}</button>
                            ))}
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-stone-200 p-3 flex gap-2 bg-stone-50">
                        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your symptoms…" className="rounded-none border-stone-300 bg-white" data-testid="ai-input" />
                        <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="ai-send"><Send className="w-4 h-4" /></Button>
                    </form>
                </div>

                {refer && (
                    <div className="mt-6 border border-amber-300 bg-amber-50 p-5 flex gap-3" data-testid="refer-banner">
                        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-medium text-amber-900">Please consult a real doctor</div>
                            <p className="text-sm text-amber-800 mt-1">Your symptom picture suggests a hands-on evaluation is wise. Talk to one of our verified homeopaths online or book an offline visit.</p>
                            <Link to="/doctors"><Button className="rounded-none mt-3 bg-amber-800 hover:bg-amber-900 text-stone-50" data-testid="refer-cta"><Stethoscope className="w-4 h-4 mr-2" />Find a doctor</Button></Link>
                        </div>
                    </div>
                )}

                {products.length > 0 && (
                    <div className="mt-10">
                        <div className="label-eyebrow text-sky-700 mb-3">Suggested from our pharmacy</div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            {products.map((p) => (
                                <Link key={p.id} to={`/product/${p.slug}`} className="border border-stone-200 bg-white group" data-testid={`ai-product-${p.slug}`}>
                                    <div className="aspect-square bg-stone-100">
                                        {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                                    </div>
                                    <div className="p-3">
                                        <div className="text-[10px] tracking-widest uppercase text-stone-500">{p.matched_remedy}</div>
                                        <h4 className="font-serif text-base mt-1">{p.name}</h4>
                                        {p.price && <div className="text-xs text-sky-700 mt-1">From {formatINR(p.price)}</div>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
                {articles.length > 0 && (
                    <div className="mt-10" data-testid="ai-articles-block">
                        <div className="label-eyebrow text-sky-700 mb-3 inline-flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" />Recommended reading from our journal</div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            {articles.map((a) => (
                                <Link key={a.id} to={`/blog/${a.slug}`} className="border border-stone-200 bg-white group" data-testid={`ai-article-${a.slug}`}>
                                    {a.cover_url ? (
                                        <div className="aspect-[16/9] bg-stone-100 overflow-hidden">
                                            <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    ) : (
                                        <div className="aspect-[16/9] bg-gradient-to-br from-sky-50 to-stone-50 flex items-center justify-center">
                                            <BookOpen className="w-8 h-8 text-stone-300" />
                                        </div>
                                    )}
                                    <div className="p-3">
                                        {a.category && <div className="text-[10px] tracking-widest uppercase text-sky-700">{a.category}</div>}
                                        <h4 className="font-serif text-base mt-1 leading-snug group-hover:text-sky-700">{a.title}</h4>
                                        {a.excerpt && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{a.excerpt}</p>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
